/**
 * Funders to cultivate (ADR-0052, homelab-kb #151).
 *
 * A SECOND list, and deliberately not a second tab on the foundations page.
 * Every funder here filed "contributes only to preselected charitable
 * organizations" with the IRS, so there is nothing to apply for. The whole
 * point of the surface is that it makes a different promise from the weekly
 * eight, and a page that looked like the lead list would quietly unmake it.
 *
 * What the card shows, and why each part is there (criteria section 3):
 *   - who the funder paid, where and how much: the only evidence a reader has
 *   - how often it takes on grantees that are new to it: the question that
 *     decides whether a relationship is worth starting, and the same number
 *     the lane's own exclusion is built on
 *   - one plain line saying there is no application process
 *
 * The page never renders a deadline or an "apply" action. Those belong to the
 * other list.
 */

import React, { useEffect, useState } from 'react'
import { getCultivate } from '../api'
import Spinner from '../components/Spinner'
import { ErrorAlert } from '../components/Alert'
import { useDocumentTitle } from '../components/PublicLayout'

export default function Cultivate() {
  useDocumentTitle('Funders to cultivate')

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let live = true
    getCultivate()
      .then((d) => live && setData(d))
      .catch((e) => live && setError(e.message || 'Could not load your list.'))
      .finally(() => live && setLoading(false))
    return () => { live = false }
  }, [])

  if (loading) return <Spinner />
  if (error) return <ErrorAlert>{error}</ErrorAlert>

  // enabled=false is NOT "none found". Saying "no funders to cultivate" to a
  // client whose lane simply has not been built yet is a false statement about
  // their corpus, which is the class of error this project keeps paying for.
  if (!data || !data.enabled) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="text-2xl font-semibold text-slate-900">Funders to cultivate</h1>
        <p className="mt-3 text-slate-600">
          This list is not switched on for your account yet. Your weekly digest
          and your foundation leads are unaffected.
        </p>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <h1 className="text-2xl font-semibold text-slate-900">Funders to cultivate</h1>
      <p className="mt-3 text-slate-600">
        These foundations fund work like yours, and each one states in its own
        IRS filing that it gives only to organizations it has already chosen.
        There is nothing to apply for, so the way in is an introduction: a board
        member, a current grantee, or someone who already knows them.
      </p>
      {data.lane_size > data.cards.length && (
        <p className="mt-2 text-sm text-slate-500">
          Showing {data.cards.length} of {data.lane_size}. Cultivating a funder
          takes months, so this list stays short on purpose.
        </p>
      )}

      {data.cards.length === 0 && (
        <p className="mt-6 text-slate-600">
          We have not found a foundation of this kind that matches your work yet.
        </p>
      )}

      <div className="mt-6 space-y-6">
        {data.cards.map((c) => (
          <article key={c.funder_id} className="rounded-lg border border-slate-200 bg-white p-5">
            <h2 className="text-lg font-semibold text-slate-900">{c.name}</h2>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
              {(c.city || c.state) && <span>{[c.city, c.state].filter(Boolean).join(', ')}</span>}
              {c.typical_grant && <span>Typical grant: {c.typical_grant}</span>}
              {c.filing_year && <span>Latest filing: {c.filing_year}</span>}
              {c.website && (
                <a className="text-indigo-600 hover:underline" href={c.website}
                   target="_blank" rel="noreferrer noopener">{c.website}</a>
              )}
            </div>

            <p className="mt-3 text-slate-700">{c.why_matched}</p>

            {c.recent_grants?.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-semibold text-slate-900">Who they funded</h3>
                <ul className="mt-2 space-y-1 text-sm text-slate-600">
                  {c.recent_grants.map((g, i) => (
                    <li key={i}>
                      {g.recipient}
                      {g.recipient_state ? ` (${g.recipient_state})` : ''}
                      {g.amount ? ` · ${g.amount}` : ''}
                      {g.year ? ` · ${g.year}` : ''}
                      {g.purpose ? <span className="block text-slate-500">{g.purpose}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="mt-4 text-sm text-slate-700">{c.new_grantees}</p>

            {c.restrictions && (
              <p className="mt-3 text-sm text-slate-600">
                <span className="font-semibold text-slate-900">Restrictions:</span> {c.restrictions}
              </p>
            )}

            <p className="mt-4 rounded bg-amber-50 p-3 text-sm text-amber-900">
              {c.no_application}
            </p>
          </article>
        ))}
      </div>
    </div>
  )
}
